/**
 * interpolateColor.js
 *
 * Design-time-only color interpolation between a 'mapping' binding's
 * numeric rules — not part of the runtime model. A rule counts as a
 * color "anchor point" when its comparison value parses as a number and
 * its result parses as a hex color; every other rule (string equality,
 * non-color results, the default rule) is simply ignored here.
 *
 * Purely a preview convenience: BindingPreview.vue may call this. No
 * generator, no migration, and no AutomationBinding field ever reads
 * from or writes to this module — see docs/Binding_Architecture_FINAL.md
 * and the "Out of Scope" list in the feature that introduced this file.
 * The underlying rules[] evaluation (evaluateMappingRules.js) is
 * completely unaffected; this only changes what BindingPreview *displays*
 * when interpolation is toggled on in the editor.
 */

/** @private */
function parseHexColor(value) {
  if (typeof value !== 'string') return null;

  const short = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(value);
  if (short) {
    return {
      r: parseInt(short[1] + short[1], 16),
      g: parseInt(short[2] + short[2], 16),
      b: parseInt(short[3] + short[3], 16),
    };
  }

  const full = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(value);
  if (full) {
    return { r: parseInt(full[1], 16), g: parseInt(full[2], 16), b: parseInt(full[3], 16) };
  }

  return null;
}

/** @private */
function formatHexColor({ r, g, b }) {
  const toHex = n => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Extract this rules[] list's usable interpolation anchor points —
 * conditioned rules (not the default) whose comparison value is numeric
 * and whose result is a valid hex color — sorted ascending by value.
 *
 * Exported so the editor's "insufficient anchor points" warning (fewer
 * than two) uses the exact same extraction as interpolateColor() itself,
 * rather than re-deriving what counts as an anchor point.
 *
 * @param {Array<{when:{operator:string,value:string}|null, result:string}>} [rules]
 * @returns {Array<{ value: number, color: {r:number,g:number,b:number} }>}
 */
export function getColorAnchors(rules) {
  return (rules ?? [])
    .filter(rule => rule.when && Number.isFinite(Number(rule.when.value)) && parseHexColor(rule.result))
    .map(rule => ({ value: Number(rule.when.value), color: parseHexColor(rule.result) }))
    .sort((a, b) => a.value - b.value);
}

/**
 * Interpolate a color for a numeric test value between this rules[]
 * list's color anchor points. Values outside the anchors' range clamp to
 * the nearest end; values between two anchors interpolate linearly per
 * RGB channel.
 *
 * @param {Array} rules
 * @param {number|string} testValue
 * @returns {string|null} a hex color, or null when interpolation isn't
 *   applicable (fewer than two anchors, or testValue isn't numeric) —
 *   callers should fall back to the normal rules[] evaluation in that case.
 */
export function interpolateColor(rules, testValue) {
  const anchors = getColorAnchors(rules);
  if (anchors.length < 2) return null;

  const t = Number(testValue);
  if (!Number.isFinite(t)) return null;

  if (t <= anchors[0].value) return formatHexColor(anchors[0].color);
  const last = anchors[anchors.length - 1];
  if (t >= last.value) return formatHexColor(last.color);

  for (let i = 0; i < anchors.length - 1; i++) {
    const from = anchors[i];
    const to   = anchors[i + 1];
    if (t >= from.value && t <= to.value) {
      const fraction = (t - from.value) / (to.value - from.value);
      return formatHexColor({
        r: from.color.r + fraction * (to.color.r - from.color.r),
        g: from.color.g + fraction * (to.color.g - from.color.g),
        b: from.color.b + fraction * (to.color.b - from.color.b),
      });
    }
  }
  return null;
}
